import { parsePhoneNumberFromString } from 'libphonenumber-js/max';

/**
 * Phone numbers are stored in E.164 (+9647XXXXXXXXX). Owner rule (O17):
 * - Iraqi mobile numbers are the default: `07XXXXXXXXX` (11 digits), also `+9647…` / `009647…`;
 * - a foreign number is accepted only when typed with `+` and its country code, and only if it is
 *   a real number for that country (libphonenumber "max" metadata, not just a digit count).
 * Spaces, dashes, dots and brackets are ignored.
 */
export type PhoneProblem =
  | 'required'
  | 'phone_characters'
  | 'phone_iraqi_format'
  | 'phone_iraqi_mobile_only'
  | 'phone_foreign_needs_plus'
  | 'phone_foreign_invalid';

export type PhoneParseResult =
  | { readonly ok: true; readonly e164: string; readonly iraqi: boolean }
  | { readonly ok: false; readonly problem: PhoneProblem };

const IRAQ_CODE = '964';
const IRAQI_MOBILE_NATIONAL = /^7\d{9}$/;

function iraqi(national: string): PhoneParseResult {
  // Some people write the trunk 0 after the country code: +964 0770 …
  const digits = national.startsWith('0') ? national.slice(1) : national;
  if (IRAQI_MOBILE_NATIONAL.test(digits)) {
    return { ok: true, e164: `+${IRAQ_CODE}${digits}`, iraqi: true };
  }
  return {
    ok: false,
    problem:
      digits.startsWith('7') || digits === '' ? 'phone_iraqi_format' : 'phone_iraqi_mobile_only',
  };
}

/** Strict parsing for anything a person types into a phone field (create, edit). */
export function parsePhone(input: string): PhoneParseResult {
  const trimmed = input.trim();
  if (trimmed === '') return { ok: false, problem: 'required' };
  const compact = trimmed.replace(/[\s\-().‎‏]/g, '');
  if (!/^\+?\d+$/.test(compact)) return { ok: false, problem: 'phone_characters' };

  if (compact.startsWith('+')) {
    const digits = compact.slice(1);
    if (digits.startsWith(IRAQ_CODE)) return iraqi(digits.slice(IRAQ_CODE.length));
    const parsed = parsePhoneNumberFromString(compact);
    if (parsed?.isValid()) return { ok: true, e164: parsed.number, iraqi: false };
    return { ok: false, problem: 'phone_foreign_invalid' };
  }
  if (compact.startsWith(`00${IRAQ_CODE}`)) return iraqi(compact.slice(2 + IRAQ_CODE.length));
  if (compact.startsWith('00')) return { ok: false, problem: 'phone_foreign_needs_plus' };
  if (/^07\d{9}$/.test(compact))
    return { ok: true, e164: `+${IRAQ_CODE}${compact.slice(1)}`, iraqi: true };
  return { ok: false, problem: 'phone_iraqi_format' };
}

/**
 * Lenient normalization used only to *look up* an existing account (sign-in, password reset):
 * accepts the older forms too (7701234567, 9647701234567), so accounts created before the strict
 * rule can still sign in. Never use it to accept a new number: use `parsePhone`.
 */
export function normalizePhone(input: string): string | undefined {
  const strict = parsePhone(input);
  if (strict.ok) return strict.e164;
  let digits = input.trim().replace(/[\s\-().]/g, '');
  if (digits.startsWith('+')) digits = digits.slice(1);
  else if (digits.startsWith('00')) digits = digits.slice(2);
  else if (/^0\d{10}$/.test(digits)) digits = `964${digits.slice(1)}`;
  else if (/^7\d{9}$/.test(digits)) digits = `964${digits}`;
  if (!/^[1-9]\d{7,14}$/.test(digits)) return undefined;
  return `+${digits}`;
}

/** Login identifier: an email (lower-cased) or a phone number (E.164). */
export function normalizeLoginIdentifier(input: string): string | undefined {
  const trimmed = input.trim();
  if (trimmed.includes('@')) {
    const email = trimmed.toLowerCase();
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) ? email : undefined;
  }
  return normalizePhone(trimmed);
}

/**
 * How people read a stored number: Iraqi mobiles in the familiar local form `0770 123 4567`,
 * foreign numbers in international form (`+44 20 7946 0958`). Unknown shapes are shown as stored.
 */
export function formatPhone(e164: string): string {
  const iraqiMobile = /^\+964(7\d{2})(\d{3})(\d{4})$/.exec(e164);
  if (iraqiMobile) return `0${iraqiMobile[1]} ${iraqiMobile[2]} ${iraqiMobile[3]}`;
  const parsed = parsePhoneNumberFromString(e164);
  return parsed ? parsed.formatInternational() : e164;
}
